import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { PageInfo } from 'generated/graphql';
import {
    MIGRATE_MESSAGES_PREVIEW_API,
    type loader,
} from '~/routes/remix-api.chat.migrate-messages-preview';
import { localizeTime } from '~/modules/root';
import { useUserLoggedIn } from '~/modules/user';
import type { DirectConversation } from '../types';

type PreviewMessages = DirectConversation['Messages']['nodes'];

/**
 * Cache for storing fetched messages by conversation ID and timestamp.
 * Key format: `${conversationID}-${fromTimestamp}`
 */
const messagesCache = new Map<
    string,
    { messages: PreviewMessages; pageInfo: PageInfo }
>();

/**
 * The user ID that the cache belongs to. Used to clear the cache when
 * the user logs out or logs in with a different account.
 */
let cacheUserID: number | null = null;

/**
 * Generate a cache key for the messages cache.
 */
function getCacheKey(conversationID: number, fromTimestamp: number): string {
    return `${conversationID}-${fromTimestamp}`;
}

/**
 * Hook to fetch conversation messages for the migrate messages preview.
 * Fetches messages in ascending order (oldest first) to display which
 * messages will be migrated when inviting users to a 1-on-1 chat.
 */
export function useMigrateMessagesPreview({
    conversationID,
    fromTimestamp,
}: {
    /**
     * The conversation ID. If set to `0`, this hook will be disabled.
     */
    conversationID: number;
    /**
     * Unix timestamp (in seconds) to filter messages from. Pass `0` to include
     * all messages, or `null` to disable fetching (no messages will be shown).
     */
    fromTimestamp: number | null;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const [messages, setMessages] = useState<PreviewMessages>([]);
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const fetcher = useFetcher<typeof loader>();
    const processedDataRef = useRef<typeof fetcher.data | null>(null);
    const fetchedFromTimestamp = useRef<number | null>(null);
    const dataFromTimestamp = useRef<number | null>(null);
    const isHookDisabled =
        conversationID <= 0 || fromTimestamp === null || !isLoggedIn;
    const apiUrl = useMemo(() => {
        const url = new URL(
            MIGRATE_MESSAGES_PREVIEW_API,
            window.location.origin,
        );
        url.searchParams.set('id', String(conversationID));
        if (fromTimestamp && fromTimestamp > 0) {
            url.searchParams.set('after', String(fromTimestamp));
        }
        return `${url.pathname}${url.search}`;
    }, [conversationID, fromTimestamp]);
    const loadMoreUrl = useMemo(() => {
        if (isHookDisabled || !messages.length || !pageInfo?.hasNextPage) {
            return null;
        }
        const url = new URL(
            MIGRATE_MESSAGES_PREVIEW_API,
            window.location.origin,
        );
        url.searchParams.set('id', String(conversationID));
        if (fromTimestamp && fromTimestamp > 0) {
            url.searchParams.set('after', String(fromTimestamp));
        }
        url.searchParams.set('afterID', messages.at(-1)!.ID);
        return `${url.pathname}${url.search}`;
    }, [conversationID, fromTimestamp, isHookDisabled, messages, pageInfo]);

    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !loadMoreUrl) {
            return;
        }
        fetcher.load(loadMoreUrl);
    }, [fetcher, loadMoreUrl]);

    // Fetch messages when the hook is enabled or parameters change.
    useEffect(() => {
        if (isHookDisabled) {
            setMessages([]);
            setPageInfo(null);
            processedDataRef.current = null;
            fetchedFromTimestamp.current = null;
            dataFromTimestamp.current = null;
            return;
        }
        if (fetchedFromTimestamp.current !== fromTimestamp) {
            // Check cache first for optimistic UI.
            const cacheKey = getCacheKey(conversationID, fromTimestamp);
            const cachedData = messagesCache.get(cacheKey);
            if (cachedData) {
                setMessages(cachedData.messages);
                // Always set hasNextPage to true to check for new messages.
                setPageInfo({ ...cachedData.pageInfo, hasNextPage: true });
                dataFromTimestamp.current = fromTimestamp;
            }
            fetcher.load(apiUrl);
            fetchedFromTimestamp.current = fromTimestamp;
        }
    }, [isHookDisabled, conversationID, apiUrl, fromTimestamp, fetcher]);

    // Process fetcher data when it arrives.
    useEffect(() => {
        if (
            isHookDisabled ||
            !fetcher.data ||
            fetcher.state !== 'idle' ||
            fetcher.data === processedDataRef.current ||
            fetchedFromTimestamp.current === null
        ) {
            return;
        }
        const conversation = fetcher.data.conversation;
        if (!conversation) return;
        const newMessages = conversation.Messages.nodes;
        const newPageInfo = conversation.Messages.pageInfo;

        if (dataFromTimestamp.current === fetchedFromTimestamp.current) {
            const currentFromTimestamp = fetchedFromTimestamp.current;
            // Cached messages or load more: Append new messages to existing ones.
            setMessages((prev) => {
                const merged = [...prev];
                for (const msg of newMessages) {
                    const msgTimestamp = localizeTime(msg.Created).unix();
                    if (
                        !merged.some((m) => m.ID === msg.ID) &&
                        msgTimestamp >= currentFromTimestamp
                    ) {
                        merged.push(msg);
                    }
                }
                // Update cache.
                const cacheKey = getCacheKey(
                    conversationID,
                    currentFromTimestamp,
                );
                messagesCache.set(cacheKey, {
                    messages: merged,
                    pageInfo: newPageInfo,
                });
                return merged;
            });
        } else {
            // Replace messages (initial fetch or parameter change).
            setMessages(newMessages);
            // Update cache.
            const cacheKey = getCacheKey(
                conversationID,
                fetchedFromTimestamp.current,
            );
            messagesCache.set(cacheKey, {
                messages: newMessages,
                pageInfo: newPageInfo,
            });
        }
        setPageInfo(newPageInfo);
        processedDataRef.current = fetcher.data;
        dataFromTimestamp.current = fetchedFromTimestamp.current;
    }, [isHookDisabled, fetcher, conversationID]);

    // Clear the cache when the user logs out or logs in with a different account.
    useEffect(() => {
        if (!isLoggedIn || user.ID !== cacheUserID) {
            messagesCache.clear();
            cacheUserID = isLoggedIn ? user.ID : null;
        }
    }, [isLoggedIn, user.ID]);

    return {
        /**
         * The messages in ascending order (oldest first).
         */
        messages,
        /**
         * Whether there are more (newer) messages to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * Total number of messages matching the filter.
         */
        totalCount: pageInfo?.totalCount ?? 0,
        /**
         * Whether the hook is currently fetching messages.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * Whether the messages list is empty after fetching.
         */
        isEmpty:
            fromTimestamp === null ||
            (dataFromTimestamp.current === fromTimestamp &&
                messages.length === 0),
        /**
         * Load more (newer) messages when scrolling down.
         */
        loadMore,
        /**
         * Clear the messages preview cache. Call this after messages have been
         * successfully migrated because the cache is not longer valid.
         */
        clearCache: useCallback(() => {
            messagesCache.clear();
        }, []),
    };
}
